<template>
  <!-- Teleported to body, positioned `fixed`. Callers live inside scroll
       containers (the checklist's `overflow-y-auto` list, the board's
       `overflow-x-auto` column strip, the editor's horizontally scrolling
       toolbar) and an absolutely positioned tooltip inside those gets clipped
       at the container's edge. Escaping to the body is the only way it
       reliably shows in full. -->
  <Teleport to="body">
    <Transition name="tooltip-fade">
      <!-- aria-hidden on purpose: every trigger already carries its own
           aria-label, so announcing this too would just read the same
           sentence twice. It is a visual affordance only. -->
      <span
        v-if="visible"
        aria-hidden="true"
        class="fixed z-[100] pointer-events-none max-w-[240px] px-2.5 py-1.5 rounded-lg text-xs font-medium leading-snug text-center shadow-lg
               bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900"
        :style="bubbleStyle"
      >
        {{ text }}
        <!-- A rotated square rather than a border triangle, so it inherits
             the bubble's own background in both themes instead of needing a
             second hard-coded colour per theme. -->
        <span
          class="absolute w-2 h-2 rotate-45 bg-gray-900 dark:bg-gray-100"
          :style="arrowStyle"
        ></span>
      </span>
    </Transition>
  </Teleport>
</template>

<script setup>
// The markup half of the app's hover tooltip. Positioning comes from
// `useTooltip()`, whose `visible`, `bubbleStyle` and `arrowStyle` feed these
// props directly.
defineProps({
  visible: { type: Boolean, default: false },
  text: { type: String, default: '' },
  bubbleStyle: { type: Object, default: () => ({}) },
  arrowStyle: { type: Object, default: () => ({}) },
})
</script>

<style scoped>
/* Opacity only, deliberately. The placement transform lives in the inline
   `:style` (it has to — it depends on runtime measurements), and an inline
   style beats any stylesheet rule that isn't !important. A `transform` here
   would therefore be silently ignored on enter and, worse, would fight the
   positioning if it ever did apply. Fading is enough at this size. */
.tooltip-fade-enter-active {
  transition: opacity 0.12s ease;
}
.tooltip-fade-leave-active {
  transition: opacity 0.09s ease;
}
.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}
</style>
